import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLayerPopUp } from '@/providers/LayerPopUpProvider';
import useAuthenticated from '@/hooks/useAuthenticated';
import {
  Address,
  QuickConsultInfoFromUntact,
  ConsultUserBasicInfo,
} from '@remodeling/remodeling-manager';
import { QuickConsultInputs } from '@/types';
import { resetFields } from '@/utils';

interface UseBasicInfoProps {
  userInfo?: ConsultUserBasicInfo;
  isUntact?: boolean;
  handleChangeAddressInfo?: (value: Address, isDefaultAddress: boolean) => void;
  untactInfo?: QuickConsultInfoFromUntact;
}

const useBasicInfo = ({
  userInfo,
  isUntact,
  handleChangeAddressInfo,
  untactInfo,
}: UseBasicInfoProps) => {
  const [isDefaultAddress, setIsDefaultAddress] = useState<boolean>(false);
  const [isZoneCode, setIsZoneCode] = useState(false);
  const formUtils = useFormContext<QuickConsultInputs>();
  const {
    setValue,
    watch,
    formState: { errors },
    register,
    getValues,
  } = formUtils;
  const { handleOpen: handleSearchZoneCodeOpen } = useLayerPopUp();
  const [siteData, setSiteData] = useState<Address>();
  const { isAuthenticated } = useAuthenticated();
  const checkUserInfo = useRef<boolean | undefined>(undefined);

  const [isNameDisabled, setIsNameDisabled] = useState<boolean>(false);
  const [isTelDisabled, setIsTelDisabled] = useState<boolean>(false);

  const setloggedInBasicInfo = useCallback(
    (userInfo: ConsultUserBasicInfo) => {
      if (!userInfo) return;

      const { name, tel, site, simpleUserFlag } = userInfo;

      if (simpleUserFlag === 'N') {
        setValue('name', name);
        setValue('tel', tel);

        setIsNameDisabled(true);
        setIsTelDisabled(true);
      }

      if (isUntact) {
        setIsDefaultAddress(false);
        return;
      }

      if (untactInfo && !untactInfo.isDefaultAddress) {
        setValue('site.addressDetail', untactInfo.site?.addressDetail);
        setValue('site.address', untactInfo.site?.address);
        setIsZoneCode(true);
        return;
      }

      site?.addressDetail && setValue('site.addressDetail', site.addressDetail);

      if (site?.address?.zoneCode) {
        setIsZoneCode(true);
        setIsDefaultAddress(true);
        setValue('site.address', site.address);
      }
    },
    [setValue, isUntact, untactInfo],
  );

  const handleChangeTelInput = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    const tel = value
      .replace(/[^0-9]/g, '')
      .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
      .replace(/(\-{1,2})$/g, ''); // eslint-disable-line
    setValue('tel', tel);
  };

  const handleClickDefaultAddress = useCallback(() => {
    if (userInfo) {
      const { site } = userInfo;

      site.addressDetail &&
        setValue('site.addressDetail', site.addressDetail || '');
      setIsDefaultAddress(true);

      if (userInfo.site?.address) {
        const { zoneCode } = site.address;

        if (zoneCode) {
          setValue('site.address.zoneCode', zoneCode || '');
          setIsZoneCode(true);
        }
        setValue('site.address', site.address || '');
      }
    }
  }, [userInfo, setValue]);

  const handleClickDirectInput = useCallback(() => {
    resetFields({
      objKeyPath: 'site.address',
      formUtils,
    });
    setValue('site.addressDetail', '');
    setIsDefaultAddress(false);
    setIsZoneCode(false);
  }, [formUtils, setValue]);

  const handleClickSearchZoneCode = () => {
    handleSearchZoneCodeOpen('SearchZoneCodeLayerPopUp');
  };

  const handleChangeZoneCode = (e: React.FormEvent<HTMLInputElement>) => {
    e.currentTarget.value ? setIsZoneCode(true) : setIsZoneCode(false);
  };

  const handleChangeSiteData = (data: Address) => {
    setSiteData(data);
  };

  const checkIsZoneCode = useCallback(() => {
    watch((value, { name }) => {
      if (name === 'site.address.zoneCode' && value.site?.address?.zoneCode)
        setIsZoneCode(true);
    });
  }, [watch]);

  const changeAddress = useCallback(() => {
    const site = getValues('site.address');
    handleChangeAddressInfo?.(site, isDefaultAddress);
  }, [getValues, handleChangeAddressInfo, isDefaultAddress]);

  const handleChangeDetailAddress = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      if (value.trim().length === 0) {
        setValue(
          'site.addressDetail',
          value.replace(/^\s*/, '').replace(/\s*$/, ''),
        );
      } else setValue('site.addressDetail', value);
    },
    [setValue],
  );

  const handleChangeName = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (value.trim().length === 0) {
      setValue('name', value.replace(/^\s*/, '').replace(/\s*$/, ''));
    } else setValue('name', value);
  };

  useEffect(() => {
    checkIsZoneCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    handleChangeAddressInfo && changeAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('site.address')]);

  useEffect(() => {
    if (!checkUserInfo.current && userInfo) {
      setloggedInBasicInfo(userInfo);
      checkUserInfo.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.name]);

  useEffect(() => {
    if (siteData) {
      setValue('site.address', siteData);
      setIsZoneCode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteData]);

  return {
    isDefaultAddress,
    isZoneCode,
    handleClickDefaultAddress,
    handleClickDirectInput,
    handleClickSearchZoneCode,
    handleChangeZoneCode,
    watch,
    formState: { errors },
    register,
    isAuthenticated,
    handleChangeTelInput,
    handleChangeSiteData,
    isNameDisabled,
    isTelDisabled,
    handleChangeDetailAddress,
    handleChangeName,
  };
};

export default useBasicInfo;
